import React, { useState } from "react";
import { useLocation } from "react-router-dom";

// Types for the donation form
interface DonationFormData {
  name: string;
  email: string;
  phone: string;
  amount: number;
  campaign: string;
  message: string;
}

const DonationForm: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const campaignId = queryParams.get("campaign");

  // Form data state
  const [formData, setFormData] = useState<DonationFormData>({
    name: "",
    email: "",
    phone: "",
    amount: 1000,
    campaign: campaignId || "general",
    message: "",
  });

  // Predefined amounts
  const donationAmounts = [500, 1000, 2000, 5000];

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle amount selection
  const handleAmountSelect = (amount: number) => {
    setFormData((prev) => ({ ...prev, amount }));
  };

  // Handle custom amount input
  const handleCustomAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amount = parseInt(e.target.value) || 0;
    setFormData((prev) => ({ ...prev, amount }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.amount < 10) {
      alert("Please enter a valid donation amount");
      return;
    }

    // Store donor information in localStorage or send to backend if needed
    localStorage.setItem(
      "donorInfo",
      JSON.stringify({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        campaign: formData.campaign,
        message: formData.message,
        amount: formData.amount,
        timestamp: new Date().toISOString(),
      })
    );

    // Direct Razorpay.me payment link without API calls
    // Simply navigate to the payment link directly
    window.location.href = "https://razorpay.me/@lifelinefoundationngo";
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
      <h3 className="text-2xl font-bold text-dark mb-6">Make a Donation</h3>

      <form onSubmit={handleSubmit}>
        {/* Personal Information */}
        <div className="space-y-4 mb-6">
          <div>
            <label
              htmlFor="name"
              className="block text-gray-700 font-medium mb-1"
            >
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              required
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-gray-700 font-medium mb-1"
            >
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              required
            />
          </div>

          <div>
            <label
              htmlFor="phone"
              className="block text-gray-700 font-medium mb-1"
            >
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
              required
            />
          </div>
        </div>

        {/* Donation Details */}
        <div className="space-y-4 mb-6">
          <div>
            <label
              htmlFor="campaign"
              className="block text-gray-700 font-medium mb-1"
            >
              Select Campaign
            </label>
            <select
              id="campaign"
              name="campaign"
              value={formData.campaign}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="general">General Donation</option>
              <option value="women">Women Empowerment</option>
              <option value="education">Rural Education</option>
              <option value="sanitation">Rural Sanitation</option>
              <option value="clean-india">Clean India Campaign</option>
              <option value="cancer">Cancer Awareness</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Select Amount (INR) *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              {donationAmounts.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className={`py-2 px-4 rounded-md border ${
                    formData.amount === amount
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-gray-700 border-gray-300 hover:border-primary"
                  }`}
                  onClick={() => handleAmountSelect(amount)}
                >
                  ₹{amount}
                </button>
              ))}
            </div>
            <div>
              <label
                htmlFor="customAmount"
                className="block text-gray-700 text-sm mb-1"
              >
                Or enter custom amount
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-600">
                  ₹
                </span>
                <input
                  type="number"
                  id="customAmount"
                  name="customAmount"
                  min="10"
                  value={
                    !donationAmounts.includes(formData.amount)
                      ? formData.amount
                      : ""
                  }
                  onChange={handleCustomAmount}
                  placeholder="Enter amount"
                  className="w-full pl-8 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-gray-700 font-medium mb-1"
            >
              Message (Optional)
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
            ></textarea>
          </div>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          className="w-full btn-primary py-3 text-lg font-semibold"
        >
          Proceed to Payment
        </button>

        <p className="text-center text-gray-600 mt-4 text-sm">
          You will be redirected to Razorpay's secure payment page. Please enter
          the amount of ₹{formData.amount} when prompted.
        </p>
      </form>
    </div>
  );
};

export default DonationForm;
